(function () {
  'use strict';

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = this.onScroll.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);

      this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 992px)');
      this.hoverTimers = new Map();

      this.initStickyBehavior();
      this.initHeaderHeight();
      this.initScrollState();
      this.relocateDrawerUtilities();
      this.bindDrawer();
      this.bindMegaMenu();
      this.bindMegaFocusOut();
      this.bindDropdowns();
      this.bindDrawerAccordions();
      this.bindSublists();
      this.bindHoverMenus();
      this.bindFocusMenus();
      this.bindSectionEvents();
      this.prewarmMenuFlips();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this._resizeTimer) window.clearTimeout(this._resizeTimer);
      if (this._headerHeightObserver) {
        this._headerHeightObserver.disconnect();
        this._headerHeightObserver = null;
      }
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      if (this._onSectionLoad) {
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
      if (window.bootstrap && bootstrap.Offcanvas && this.drawer) {
        const instance = bootstrap.Offcanvas.getInstance(this.drawer);
        if (instance) instance.dispose();
      }
      document.querySelectorAll('.offcanvas-backdrop').forEach((backdrop) => backdrop.remove());
      document.body.classList.remove('offcanvas-backdrop', 'modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      this.clearHoverTimers();
    }

    bindSectionEvents() {
      this._onSectionLoad = (event) => {
        if (event.target.contains(this) || event.target === this) {
          this.initStickyBehavior();
          this.initHeaderHeight();
          this.initScrollState();
          this.relocateDrawerUtilities();
          this.bindDrawer();
          this.bindMegaMenu();
          this.bindMegaFocusOut();
          this.bindDropdowns();
          this.bindDrawerAccordions();
          this.bindSublists();
          this.bindHoverMenus();
          this.bindFocusMenus();
          this.prewarmMenuFlips();
        }
      };
      document.addEventListener('shopify:section:load', this._onSectionLoad);
    }

    hoverEnabled() {
      return this.hoverMedia.matches;
    }
 
    isKeyboardFocus(el) {
      try {
        return !!el && el.matches(':focus-visible');
      } catch (error) {
        return false;
      }
    }

    // True when a keyboard-focused element lives inside `el`. Used to veto
    // hover closes so a menu never disappears from under keyboard focus.
    hasKeyboardFocusWithin(el) {
      const active = document.activeElement;
      return !!el && !!active && el.contains(active) && this.isKeyboardFocus(active);
    }

    scheduleMegaClose() {
      this.scheduleClose('mega', () => {
        const item = this.activeMegaTrigger?.closest('.header-menu__item');
        if (item && this.hasKeyboardFocusWithin(item)) return;
        this.closeMegaMenu();
      });
    }

    scheduleClose(key, callback, delay = 200) {
      this.cancelClose(key);
      this.hoverTimers.set(
        key,
        window.setTimeout(() => {
          this.hoverTimers.delete(key);
          callback();
        }, delay)
      );
    }

    cancelClose(key) {
      const id = this.hoverTimers.get(key);
      if (id) {
        window.clearTimeout(id);
        this.hoverTimers.delete(key);
      }
    }

    clearHoverTimers() {
      this.hoverTimers?.forEach((id) => window.clearTimeout(id));
      this.hoverTimers?.clear();
    }

    bindHoverMenus() {
      this.clearHoverTimers();

      this.querySelectorAll('.header-menu__nav .header-menu__item').forEach((item) => {
        const details = item.querySelector(':scope > .header-menu__details');
        if (!details || item.dataset.hoverBound === 'true') return;
        item.dataset.hoverBound = 'true';

        item.addEventListener('mouseenter', () => {
          if (!this.hoverEnabled()) return;
          this.cancelClose(item);
          delete item.dataset.focusOpenSuppressed;

          if (details.dataset.megaOverride === 'true') {
            const trigger = item.querySelector('[data-mega-trigger-link]');
            if (trigger && this.megaMap?.has(trigger)) {
              this.cancelClose('mega');
              const panel = this.megaMap.get(trigger);
              if (panel?.hidden) this.openMegaMenu(trigger);
              return;
            }
          }

          this.closeMegaMenu();
          this.cancelClose('mega');
          if (!details.open) {
            details.open = true;
            this.updateMenuFlip(details);
          }
        });

        item.addEventListener('mouseleave', () => {
          if (!this.hoverEnabled()) return;
          if (details.dataset.megaOverride === 'true') {
            this.scheduleMegaClose();
          }
          this.scheduleClose(item, () => {
            if (this.hasKeyboardFocusWithin(item)) return;
            details.open = false;
          });
        });
      });

      this.querySelectorAll('.header-menu__nav .header-menu__dropdown-item').forEach((item) => {
        const sublist = item.querySelector(':scope > .header-menu__sublist');
        if (!sublist || item.dataset.hoverSublistBound === 'true') return;
        item.dataset.hoverSublistBound = 'true';

        const link = item.querySelector(':scope > .header-menu__dropdown-link');

        item.addEventListener('mouseenter', () => {
          if (!this.hoverEnabled()) return;
          this.cancelClose(item);
          this.closeSublists(item);
          item.classList.add('is-open');
          link?.setAttribute('aria-expanded', 'true');
          this.updateSublistFlip(item);
        });

        item.addEventListener('focusin', () => {
          this.closeSublists(item);
          item.classList.add('is-open');
          link?.setAttribute('aria-expanded', 'true');
          this.updateSublistFlip(item);
        });

        item.addEventListener('focusout', (event) => {
          const next = event.relatedTarget;
          if (!next || item.contains(next)) return;
          item.classList.remove('is-open');
          link?.setAttribute('aria-expanded', 'false');
        });

        item.addEventListener('mouseleave', () => {
          if (!this.hoverEnabled()) return;
          this.scheduleClose(item, () => {
            if (this.hasKeyboardFocusWithin(item)) return;
            item.classList.remove('is-open');
            link?.setAttribute('aria-expanded', 'false');
          });
        });
      });

      (this.megaTriggers || []).forEach((trigger) => {
        if (!this.megaMap?.has(trigger) || trigger.dataset.hoverMegaBound === 'true') return;
        trigger.dataset.hoverMegaBound = 'true';

        const item = trigger.closest('.header-menu__item') || trigger;
        if (item.querySelector(':scope > .header-menu__details')) return;

        item.addEventListener('mouseenter', () => {
          if (!this.hoverEnabled()) return;
          this.cancelClose('mega');
          const panel = this.megaMap.get(trigger);
          if (panel?.hidden) this.openMegaMenu(trigger);
        });

        item.addEventListener('mouseleave', () => {
          if (!this.hoverEnabled()) return;
          this.scheduleMegaClose();
        });
      });

      (this.megaPanels || []).forEach((panel) => {
        if (panel.dataset.hoverPanelBound === 'true') return;
        panel.dataset.hoverPanelBound = 'true';

        panel.addEventListener('mouseenter', () => {
          if (this.hoverEnabled()) this.cancelClose('mega');
        });

        panel.addEventListener('mouseleave', () => {
          if (this.hoverEnabled()) this.scheduleMegaClose();
        });
      });

    }

    // Keyboard menu of bindHoverMenus(): 
      
    bindFocusMenus() {
      this.querySelectorAll('.header-menu__nav .header-menu__item').forEach((item) => {
        const details = item.querySelector(':scope > .header-menu__details');
        if (!details || item.dataset.focusBound === 'true') return;
        item.dataset.focusBound = 'true';

        item.addEventListener('focusin', (event) => {
          if (!this.isKeyboardFocus(event.target)) return;
          if (item.dataset.focusOpenSuppressed === 'true') return;
          this.cancelClose(item);

          if (details.dataset.megaOverride === 'true') {
            const trigger = item.querySelector('[data-mega-trigger-link]');
            if (trigger && this.megaMap?.has(trigger)) {
              this.cancelClose('mega');
              const panel = this.megaMap.get(trigger);
              if (panel?.hidden) this.openMegaMenu(trigger);
              return;
            }
          }

          this.closeMegaMenu();
          this.cancelClose('mega');
          if (!details.open) {
            details.open = true;
            this.updateMenuFlip(details);
          }
        });

        item.addEventListener('focusout', (event) => {
          const next = event.relatedTarget;
          if (!next || item.contains(next)) return;
          delete item.dataset.focusOpenSuppressed;
          if (details.open) details.open = false;
        });
      });

      (this.megaTriggers || []).forEach((trigger) => {
        if (!this.megaMap?.has(trigger) || trigger.dataset.focusMegaBound === 'true') return;
        trigger.dataset.focusMegaBound = 'true';

        const item = trigger.closest('.header-menu__item') || trigger;
        if (item.querySelector(':scope > .header-menu__details')) return;

        item.addEventListener('focusin', (event) => {
          if (!this.isKeyboardFocus(event.target)) return;
          this.cancelClose('mega');
          const panel = this.megaMap.get(trigger);
          if (panel?.hidden) this.openMegaMenu(trigger);
        }); 
      });
    }

    initStickyBehavior() {
      const section = this.closest('.shopify-section');
      if (!section) return;

      if (this.dataset.sticky === 'true') {
        section.style.position = 'sticky';
        section.style.top      = '0';
        section.style.zIndex   = '30';
        section.style.width    = '100%';
      } else { 
        section.style.position = '';
        section.style.top      = '';
        section.style.zIndex   = '';
        section.style.width    = '';
      }
    }

    
    initHeaderHeight() {
      const inner = this.querySelector('.site-header__inner');
      if (!inner) return;

      const apply = () => {
        document.documentElement.style.setProperty('--header-height', `${Math.round(inner.offsetHeight)}px`);
      };
      apply();

      if (!('ResizeObserver' in window)) return;
      if (this._headerHeightObserver) this._headerHeightObserver.disconnect();
      this._headerHeightObserver = new ResizeObserver(apply);
      this._headerHeightObserver.observe(inner);
    }

    initScrollState() {
      this.onScroll();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('site-header--ready'));
      });
    }

    onScroll() {
      const isScrolled = window.scrollY > 15;
      this.classList.toggle('site-header--scrolled', isScrolled);
    }

    // header drawer utilities
    relocateDrawerUtilities() {
      const target = this.querySelector('[data-drawer-utilities-target]');
      if (!target) return;

      const source = this.querySelector('[data-drawer-utilities]');
      if (!source) {
        target.hidden = true;
        return;
      }

      const host = target.querySelector('.offcanvas-footer') || target;
      if (source.parentElement !== host) host.appendChild(source);
      source.hidden = false;
      target.hidden = false;
    }

    bindDrawer() {
      this.drawer = this.querySelector('[data-menu-drawer]');
      this.drawerOpeners = this.querySelectorAll('[data-menu-drawer-open]');
      this.drawerCloser = this.querySelector('[data-menu-drawer-close]');
      this.lastDrawerTrigger = null;

      this.drawerOpeners.forEach((button) => {
        button.addEventListener('click', () => this.openDrawer(button));
      });

      this.drawerCloser?.addEventListener('click', () => this.closeDrawer());
 
      this.drawer?.addEventListener('hidden.bs.offcanvas', () => {
        this.drawerOpeners.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        this.lastDrawerTrigger?.focus();
      });
    }

    getDrawerOffcanvas() {
      if (window.bootstrap && bootstrap.Offcanvas && this.drawer) {
        return bootstrap.Offcanvas.getOrCreateInstance(this.drawer);
      }
      return null;
    }

    isDrawerOpen() {
      return Boolean(this.drawer?.classList.contains('show'));
    }

    openDrawer(trigger) {
      if (!this.drawer || this.isDrawerOpen()) return;
      this.lastDrawerTrigger = trigger;
      const offcanvas = this.getDrawerOffcanvas();
      if (!offcanvas) return;
      trigger?.setAttribute('aria-expanded', 'true');
      offcanvas.show();
    }

    closeDrawer() {
      if (!this.isDrawerOpen()) return;
      this.getDrawerOffcanvas()?.hide();
    }

    bindMegaMenu() {
      this.megaPanels = Array.from(this.querySelectorAll('[data-mega-panel]'));
      this.megaTriggers = Array.from(this.querySelectorAll('[data-mega-trigger-link]'));
      this.megaMap = new Map();
      this.activeMegaTrigger = null;

      if (!this.megaPanels.length) return; 
      this.megaPanels.forEach((panel) => {
        const title = (panel.dataset.megaTriggerTitle || '').trim().toLowerCase();
        if (!title) return;

        const alreadyMapped = this.megaTriggers.some((trigger) => {
          const handle = trigger.dataset.megaTriggerHandle;
          if (handle && handle === panel.dataset.megaTriggerHandle) return true;
          return (trigger.textContent || '').trim().toLowerCase() === title;
        });
        if (alreadyMapped) return;

        Array.from(
          this.querySelectorAll(
            '.header-menu__nav .header-menu__item > .header-menu__link, .header-menu__nav .header-menu__item .header-menu__link-text'
          )
        )
          .filter((link) => (link.textContent || '').trim().toLowerCase() === title)
          .forEach((link) => { 
            const details = link.closest('.header-menu__details');
            const trigger = link;
            trigger.setAttribute('aria-haspopup', 'true');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.dataset.megaTriggerLink = '';
            trigger.dataset.megaTriggerHandle = panel.dataset.megaTriggerHandle || '';
            if (details && details.dataset.megaOverride !== 'true') {
              details.dataset.megaOverride = 'true';
              const summary = details.querySelector(':scope > summary');
              summary?.addEventListener('click', (event) => {
                const onTrigger = event.target === trigger || trigger.contains(event.target);
                if (onTrigger && this.hoverEnabled() && this.activeMegaTrigger === trigger) {
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
                if (!onTrigger) {
                  trigger.click();
                }
              });
            }
            this.addMegaChevron(trigger);
            this.megaTriggers.push(trigger);
          });
      });

      this.megaTriggers.forEach((trigger) => {
        const handle = trigger.dataset.megaTriggerHandle;
        let panel = handle
          ? this.megaPanels.find((candidate) => candidate.dataset.megaTriggerHandle === handle)
          : null;

        if (!panel) {
          const text = (trigger.textContent || '').trim().toLowerCase();
          panel = this.megaPanels.find(
            (candidate) => (candidate.dataset.megaTriggerTitle || '').trim().toLowerCase() === text
          );
        }

        if (!panel) return;

        this.megaMap.set(trigger, panel);
        trigger.setAttribute('aria-controls', panel.id); 
        const item = trigger.closest('.header-menu__item');
        if (item && panel.parentElement !== item) item.appendChild(panel);

        if (trigger.dataset.megaBound === 'true') return;
        trigger.dataset.megaBound = 'true';

        trigger.addEventListener('click', (event) => {
          const target = this.megaMap.get(trigger);
          if (this.hoverEnabled() && target && !target.hidden && this.activeMegaTrigger === trigger) {
            return;
          }
          event.preventDefault();
          if (trigger.closest('.header-menu__details')) event.stopPropagation();
          if (target && target.hidden) {
            this.openMegaMenu(trigger);
          } else {
            this.closeMegaMenu();
          }
        });
      });

      this.megaPanels.forEach((panel) => this.bindMegaTabs(panel));
    }
 
    bindMegaFocusOut() {
      if (this.dataset.megaFocusBound === 'true') return;
      this.dataset.megaFocusBound = 'true';

      this.addEventListener('focusout', (event) => {
        if (!this.activeMegaTrigger) return; 
        const next = event.relatedTarget;
        if (!next) return;

        const item = this.activeMegaTrigger.closest('.header-menu__item');
        const panel = this.megaMap?.get(this.activeMegaTrigger);
        if (item?.contains(next) || panel?.contains(next)) return;

        this.closeMegaMenu();
      });
    }

    addMegaChevron(link) {
      if (link.querySelector('.header-menu__chevron') || link.closest('summary')?.querySelector('.header-menu__chevron')) return;

      const chevron = document.createElement('span');
      chevron.className = 'header-menu__chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">' +
        '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" ' +
        'stroke-width="var(--icon-stroke-width)" d="m5 8 5 5 5-5"></path></svg>';
      link.appendChild(chevron);
    }

    openMegaMenu(trigger) {
      const panel = this.megaMap?.get(trigger);
      if (!panel) return;

      this.closeMegaMenu();
      this.closeAllDropdowns();

      this.activeMegaTrigger = trigger;
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      const triggerDetails = trigger.closest('.header-menu__details');
      if (triggerDetails) {
        triggerDetails.classList.add('is-open');
        const triggerSummary = triggerDetails.querySelector(':scope > summary');
        triggerSummary?.setAttribute('aria-expanded', 'true');
        triggerSummary?.classList.add('is-open');
      }
      panel.querySelectorAll('[data-fc-swiper]').forEach((carousel) => {
        if (window.SectionCarousel && carousel.dataset.megaReady !== 'true') {
          carousel.dataset.megaReady = 'true';
          window.SectionCarousel.reinit(carousel);
        } else if (carousel.swiperInstance) {
          carousel.swiperInstance.update();
        }
      });
    }

    closeMegaMenu() {
      (this.megaPanels || []).forEach((panel) => {
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
      });
      (this.megaTriggers || []).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        const triggerDetails = trigger.closest('.header-menu__details');
        if (triggerDetails && !triggerDetails.open) {
          triggerDetails.classList.remove('is-open');
          const triggerSummary = triggerDetails.querySelector(':scope > summary');
          triggerSummary?.setAttribute('aria-expanded', 'false');
          triggerSummary?.classList.remove('is-open');
        }
      });
      this.activeMegaTrigger = null;
    }

    bindMegaTabs(panel) {
      if (panel.dataset.megaTabsBound === 'true') return;

      const tabs = Array.from(panel.querySelectorAll('[data-mega-tab]'));
      const panes = Array.from(panel.querySelectorAll('[data-mega-tab-panel]')); 
      if (!tabs.length && panes.length) {
        panes[0].hidden = false;
        panel.dataset.megaTabsBound = 'true';
        return;
      }
      if (!tabs.length || !panes.length) return;

      panel.dataset.megaTabsBound = 'true';

      const paneFor = (tab) => {
        const title = (tab.dataset.megaTabTitle || '').trim();
        return (
          panes.find((pane) => (pane.dataset.megaTabTitle || '').trim() === title) ||
          panes.find((pane) => !(pane.dataset.megaTabTitle || '').trim())
        );
      };

      const activate = (tab) => {
        const target = paneFor(tab);
        tabs.forEach((candidate) => {
          const selected = candidate === tab;
          candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
          candidate.tabIndex = selected ? 0 : -1;
        });
        panes.forEach((pane) => {
          pane.hidden = pane !== target;
        });
        if (target && target.id) tab.setAttribute('aria-controls', target.id);
      };

      tabs.forEach((tab) => {
        tab.addEventListener('mouseenter', () => activate(tab));
        tab.addEventListener('focus', () => activate(tab));
        tab.addEventListener('click', (event) => { 
          if (tab.getAttribute('aria-selected') !== 'true') {
            event.preventDefault();
            activate(tab);
          }
        });
        tab.addEventListener('keydown', (event) => {
          const index = tabs.indexOf(tab);
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            tabs[(index + 1) % tabs.length].focus();
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            tabs[(index - 1 + tabs.length) % tabs.length].focus();
          }
        });
      });

      activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    }

    updateMenuFlip(details) {
      const dropdown = details.querySelector(':scope > .header-menu__dropdown');
      if (!dropdown || !details.open) return;
      const margin = 8;
      const viewportWidth = document.documentElement.clientWidth;

      // Reset before measuring, otherwise the shift compounds on reopen/resize.
      dropdown.style.setProperty('--header-menu-shift', '0px');
      const rect = dropdown.getBoundingClientRect();

      let shift = 0;
      if (rect.right > viewportWidth - margin) {
        shift = -(rect.right - (viewportWidth - margin));
      }
      if (rect.left + shift < margin) {
        shift = margin - rect.left;
      }

      if (shift) dropdown.style.setProperty('--header-menu-shift', `${Math.round(shift)}px`);
    }

     
    prewarmMenuFlips() {
      if (window.innerWidth < 992) return;
      this.querySelectorAll('.header-menu__details').forEach((details) => {
        if (details.open) return;
        details.open = true;
        this.updateMenuFlip(details);
        details.open = false;
      });
    }

    updateSublistFlip(item) {
      const sublist = item.querySelector(':scope > .header-menu__sublist');
      if (!sublist) return;
      const margin = 8;
      const viewportWidth = document.documentElement.clientWidth;

      sublist.classList.remove('header-menu__sublist--flip');
      if (sublist.getBoundingClientRect().right > viewportWidth - margin) {
        sublist.classList.add('header-menu__sublist--flip');
        if (sublist.getBoundingClientRect().left < margin) {
          sublist.classList.remove('header-menu__sublist--flip');
        }
      }
    }

    onResize() {
      if (
        this.isDrawerOpen() &&
        window.innerWidth >= 992 &&
        !this.classList.contains('site-header--drawer')
      ) {
        this.closeDrawer();
      }
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = window.setTimeout(() => {
        this.querySelectorAll('.header-menu__details[open]').forEach((details) => {
          this.updateMenuFlip(details);
        });
        this.querySelectorAll('.header-menu__dropdown-item.is-open').forEach((item) => {
          this.updateSublistFlip(item);
        });
        this.prewarmMenuFlips();
      }, 150);
    }

    bindDropdowns() {
      this.querySelectorAll('.header-menu__details').forEach((details) => {
        
        if (details.dataset.dropdownBound === 'true') return;
        details.dataset.dropdownBound = 'true';

        const summary = details.querySelector('.header-menu__link--summary');
        summary?.setAttribute('aria-expanded', 'false');
        summary?.setAttribute('aria-haspopup', 'true');

        details.addEventListener('toggle', () => {
          const isOpen = details.open;
          summary?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          details.classList.toggle('is-open', isOpen);
          summary?.classList.toggle('is-open', isOpen);
          if (isOpen) {
            this.closeOtherDropdowns(details);
            this.updateMenuFlip(details);
          } else {
            this.closeSublists(); 
            const item = details.closest('.header-menu__item');
            if (item && item.contains(document.activeElement)) {
              item.dataset.focusOpenSuppressed = 'true';
            }
          }
        });
       });
    }

    bindDrawerAccordions() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.querySelectorAll('.header-menu__drawer-details').forEach((details) => {
        if (details.dataset.accordionBound === 'true') return;
        details.dataset.accordionBound = 'true';

        const summary = details.querySelector(':scope > .header-menu__drawer-summary');
        const collapse = details.querySelector(':scope > .header-menu__drawer-collapse');
        if (!summary || !collapse) return;

        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        if (details.open) details.classList.add('is-expanded');

        summary.addEventListener('click', (event) => {
          if (event.target.closest('a')) return;
          if (!details.open) return;
          event.preventDefault();

          if (!details.classList.contains('is-expanded')) { 
            window.clearTimeout(details._collapseTimer);
            details.classList.add('is-expanded');
            summary.setAttribute('aria-expanded', 'true');
            return;
          }

          details.classList.remove('is-expanded');
          summary.setAttribute('aria-expanded', 'false');

          if (reduceMotion.matches) {
            details.open = false;
            return;
          }

          const finish = (event) => {
            if (event && event.target !== collapse) return;
            window.clearTimeout(details._collapseTimer);
            collapse.removeEventListener('transitionend', finish);
            if (!details.classList.contains('is-expanded')) details.open = false;
          };
          collapse.addEventListener('transitionend', finish);
          details._collapseTimer = window.setTimeout(finish, 400);
        });

        details.addEventListener('toggle', () => {
          if (details.open) {
            window.clearTimeout(details._collapseTimer);
            summary.setAttribute('aria-expanded', 'true');
            requestAnimationFrame(() => {
              requestAnimationFrame(() => details.classList.add('is-expanded'));
            });
          }
        });
      });
    }

    closeOtherDropdowns(exceptDetails) {
      this.querySelectorAll('.header-menu__details[open]').forEach((details) => {
        if (details !== exceptDetails) details.open = false;
      });
    }

    closeAllDropdowns() {
      this.querySelectorAll('.header-menu__details[open]').forEach((details) => {
        details.open = false;
      });
      this.closeSublists();
    }

    bindSublists() { 
      this.querySelectorAll('.header-menu__dropdown-item').forEach((item) => {
        const sublist = item.querySelector(':scope > .header-menu__sublist');
        if (!sublist) return;

        if (item.dataset.sublistBound === 'true') return;
        item.dataset.sublistBound = 'true';

        const link = item.querySelector(':scope > .header-menu__dropdown-link');
        link?.setAttribute('aria-haspopup', 'true');
        link?.setAttribute('aria-expanded', 'false');

        link?.addEventListener('click', (event) => {
          if (!item.classList.contains('is-open')) {
            event.preventDefault();
            this.closeSublists(item);
            item.classList.add('is-open');
            link.setAttribute('aria-expanded', 'true');
            this.updateSublistFlip(item);
          }
        });
      });
    }

    closeSublists(exceptItem) {
      this.querySelectorAll('.header-menu__dropdown-item.is-open').forEach((item) => {
        if (item === exceptItem) return;
        item.classList.remove('is-open');
        item
          .querySelector(':scope > .header-menu__dropdown-link')
          ?.setAttribute('aria-expanded', 'false');
      });
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeMegaMenu();
        this.closeAllDropdowns();
        return;
      }

      if (!event.target.closest('[data-mega-trigger-link], [data-mega-panel]')) {
        this.closeMegaMenu();
      }

      if (!event.target.closest('.header-menu__details')) {
        this.closeAllDropdowns();
      } else if (!event.target.closest('.header-menu__dropdown-item')) {
        this.closeSublists();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') { 
        const megaTrigger = this.contains(document.activeElement) ? this.activeMegaTrigger : null;

        this.closeMegaMenu();
        this.closeSublists();

        if (megaTrigger) {
          megaTrigger.focus();
          return;
        }

        const openDropdown = this.querySelector('.header-menu__details[open]');
        if (openDropdown) {
          openDropdown.open = false;
          openDropdown.querySelector('.header-menu__link--summary')?.focus();
        }
      }
    }
  }

  if (!customElements.get('site-header')) {
    customElements.define('site-header', SiteHeader);
  }
})();
